/**
 * Call Change Proposals data access (#312, increment B).
 *
 * A phone call about an existing Booking produces proposed edits; the
 * Operator decides each one here. Accept is the feature's only write
 * path — a 409 means the field changed since the call (the server
 * marks the proposal superseded) or the proposal was already decided.
 */
import { apiFetch } from "../../../shared/api-client";

export type CallProposalKind =
  | "set_booking_field"
  | "set_customer_field"
  | "set_line_qty"
  | "remove_line"
  | "add_line"
  | "append_note";

export interface CallProposal {
  id: string;
  phone_call_id: string;
  kind: CallProposalKind | string;
  target: string;
  proposed: unknown;
  current: unknown;
  transcript_span: string;
  status: "proposed" | "accepted" | "rejected" | "superseded" | string;
  decided_at: string | null;
  created_at: string | null;
  from_e164: string;
  call_started_at: string | null;
}

export interface NeedsBookingCall {
  id: string;
  from_e164: string;
  started_at: string | null;
  candidate_ids: string[];
}

export interface CallProposalsRead {
  proposals: CallProposal[];
  needsBookingCalls: NeedsBookingCall[];
}

export function listCallProposals(
  bookingId: string,
  signal?: AbortSignal,
): Promise<CallProposalsRead> {
  return apiFetch<CallProposalsRead>(
    `/api/admin/events/${bookingId}/call-proposals`,
    { signal },
  );
}

export function decideCallProposal(
  id: string,
  action: "accept" | "reject",
): Promise<{ status: string }> {
  return apiFetch<{ status: string }>("/api/admin/call-proposals/decide", {
    method: "POST",
    body: { id, action },
  });
}

export function chooseCallBooking(
  callId: string,
  bookingId: string,
): Promise<{ status: string }> {
  return apiFetch<{ status: string }>("/api/admin/calls/choose-booking", {
    method: "POST",
    body: { callId, bookingId },
  });
}

export function dismissCallBooking(
  callId: string,
  bookingId: string,
): Promise<{ status: string }> {
  return apiFetch<{ status: string }>("/api/admin/calls/dismiss-booking", {
    method: "POST",
    body: { callId, bookingId },
  });
}
